
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePwaInstall } from '@/hooks/usePwaInstall';

const PwaInstallButton: React.FC = () => {
  const { deferredPrompt, isInstallable, isIOSDevice, appInstalled, handleInstallClick } = usePwaInstall();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [bannerClosed, setBannerClosed] = useState(false);
  const { user } = useAuth();
  const isMobile = useIsMobile();

  React.useEffect(() => {
    // Verify if the banner has been closed in this session
    const bannerClosedInSession = sessionStorage.getItem('pwa-banner-closed') === 'true';
    setBannerClosed(bannerClosedInSession);
  }, []);

  const closeBanner = () => {
    console.log("Banner de instalación cerrado por el usuario");
    setBannerClosed(true);
    sessionStorage.setItem('pwa-banner-closed', 'true');
  };
  
  // If the app is already installed or banner was closed, or user is not logged in, don't show the button
  if (appInstalled || bannerClosed || !user) {
    return null;
  }

  // Si no es dispositivo móvil y no es instalable, no mostrar el botón
  if (!isMobile && !isInstallable && !isIOSDevice) {
    console.log("No mostrando banner en desktop no instalable");
    return null;
  }

  return (
    <>
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
        <div className="flex items-center bg-gradient-to-r from-green-500 to-green-700 text-white p-3 rounded-lg shadow-lg relative">
          <button 
            onClick={closeBanner} 
            className="absolute -right-3 -top-3 text-white bg-black/60 rounded-full p-2 hover:bg-black/80"
            aria-label="Cerrar banner"
          >
            <X className="h-4 w-4" />
          </button>
          
          {isIOSDevice ? (
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => setShowIOSInstructions(!showIOSInstructions)}
              className="text-sm bg-white text-green-600 hover:bg-white/90 font-medium"
            >
              {showIOSInstructions ? 'Ocultar' : 'Instalar App'}
            </Button>
          ) : (
            <Button
              onClick={handleInstallClick}
              variant="secondary"
              size="sm"
              className="text-sm bg-white text-green-600 hover:bg-white/90 font-medium"
            >
              Instalar <Download className="ml-1 h-4 w-4" />
            </Button>
          )}
          
          {isIOSDevice && showIOSInstructions && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-3 bg-white text-gray-800 rounded-lg shadow-lg text-xs w-60">
              <p className="font-semibold mb-2">Para instalar esta app:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Pulsa el botón compartir en Safari</li>
                <li>Selecciona "Añadir a Pantalla de inicio"</li>
                <li>Confirma tocando "Añadir"</li>
              </ol>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PwaInstallButton;
